/*
 * Copyright (c) 2026. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import _forEach from "lodash/forEach";
import _isBoolean from "lodash/isBoolean";
import _reduce from "lodash/reduce";
import formSteps from "./formSteps";
import { fromDate, fromFloat, fromNumber, sanitizePersonData, toDate, toFloat } from "@/src/services/formServiceFunctions";
import { FormServiceBase, FormServiceSaveProps } from "@/src/types/FormServiceBase";
import { Customer } from "@/src/types/Entities/Customer";
import { ProcessCostSupport } from "@/src/types/Entities/ProcessCostSupport";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [key in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_INTRO]: {
      processCostSupport: [],
    },
    [applicationStatuses.APPLICATION_STATUS_LEGAL_EXPENSES_INSURANCE]: {
      processCostSupport: ["isCostCovered", "costCoverageAmount", "hasPotentialCoverage", "potentialCoverer"],
    },
    [applicationStatuses.APPLICATION_STATUS_PERSONAL]: {
      petitioner: ["gender", "givenName", "familyName", "birthName", "birthDate"],

      "petitioner.addresses[0]": ["streetAddress", "postalCode", "addressLocality"],

      processCostSupport: ["telephone"],
    },
    [applicationStatuses.APPLICATION_STATUS_JOB]: {
      processCostSupport: ["job"],
    },
    [applicationStatuses.APPLICATION_STATUS_REPRESENTATIVE]: {
      processCostSupport: ["hasLegalRepresentative", "telephoneRepresentative"],
      legalRepresentative: ["gender", "givenName", "familyName"],
      "legalRepresentative.addresses[0]": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_MAINTENANCE_CLAIM]: {
      alimonyObligatedPerson: ["gender", "givenName", "familyName"],
      processCostSupport: ["hasAlimonyClaims", "alimonyObligatedPersonRelation"],
    },
    [applicationStatuses.APPLICATION_STATUS_ALIMONY]: {
      processCostSupport: ["hasRelativesAlimonyClaims", "alimonyPersons"],
    },
    [applicationStatuses.APPLICATION_STATUS_SOCIAL_WELFARE]: {
      processCostSupport: ["hasSocialWelfare"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_EMPLOYMENT]: {
      processCostSupport: ["hasIncomeEmployment", "incomeEmploymentAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_SELFEMPLOYMENT]: {
      processCostSupport: ["hasIncomeSelfEmployment", "incomeSelfEmploymentAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_RENT]: {
      processCostSupport: ["hasIncomeRent", "incomeRentAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_ASSETS]: {
      processCostSupport: ["hasIncomeAssets", "incomeAssetsAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_CHILD_ALLOWANCE]: {
      processCostSupport: ["hasIncomeChildAllowance", "incomeChildAllowanceAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_LIVING]: {
      processCostSupport: ["hasIncomeLiving", "incomeLivingAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_ALIMONY]: {
      processCostSupport: ["hasIncomeAlimony", "incomeAlimonyAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_PENSION]: {
      processCostSupport: ["hasIncomePension", "incomePensionAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_UNEMPLOYED_ALLOWANCE_1]: {
      processCostSupport: ["hasIncomeUnemploymentAllowance", "incomeUnemploymentAllowanceAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_UNEMPLOYED_ALLOWANCE_2]: {
      processCostSupport: ["hasIncomeUnemploymentAllowance2", "incomeUnemploymentAllowance2Amount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_SICKNESS]: {
      processCostSupport: ["hasIncomeSickness", "incomeSicknessAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_PARENTAL_ALLOWANCE]: {
      processCostSupport: ["hasIncomeParentalAllowance", "incomeParentalAllowanceAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_OTHER]: {
      processCostSupport: [
        "hasIncomeOther1",
        "hasIncomeOther2",
        "incomeOther1Name",
        "incomeOther2Name",
        "incomeOther1Amount",
        "incomeOther2Amount",
        "incomeOther1Period",
        "incomeOther2Period",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_INCOME_DESCRIPTIVE]: {
      processCostSupport: ["incomeDescription"],
    },
    [applicationStatuses.APPLICATION_STATUS_BANK_ACCOUNT]: {
      processCostSupport: ["hasBankAccount", "bankAccounts"],
    },
    [applicationStatuses.APPLICATION_STATUS_REALESTATE]: {
      processCostSupport: [
        "hasRealEstate",
        "realEstate1Size",
        "realEstate1Location",
        "realEstate1Ownership",
        "realEstate1NumberApartments",
        "realEstate1Value",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_VEHICLES]: {
      processCostSupport: ["hasVehicles", "vehicles"],
    },
    [applicationStatuses.APPLICATION_STATUS_CASH]: {
      processCostSupport: ["cashAmount", "hasValuables", "valuables"],
    },
    [applicationStatuses.APPLICATION_STATUS_PENSION_INSURANCE]: {
      processCostSupport: ["hasPensionInsurance", "pensionInsurances"],
    },
    [applicationStatuses.APPLICATION_STATUS_OTHER_ASSETS]: {
      processCostSupport: ["hasOtherAssets", "otherAssets"],
    },
    [applicationStatuses.APPLICATION_STATUS_TAX]: {
      processCostSupport: ["hasTaxDecuction", "taxAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_SOCIAL_SECURITY]: {
      processCostSupport: ["hasSocialSecurityDeduction", "socialSecurityAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_INSURANCES]: {
      processCostSupport: ["hasOtherInsuranceDeduction", "otherInsuranceAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_MOBILITY]: {
      processCostSupport: ["hasMobilityCostDeduction", "mobilityCostUnit", "mobilityCostAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_OTHER_EXPENSES]: {
      processCostSupport: ["hasOtherExpensesDeduction", "otherExpensesAmount"],
    },
    [applicationStatuses.APPLICATION_STATUS_LIVING_TYPE]: {
      processCostSupport: [
        "livingType",
        "appartmentSize",
        "numberRooms",
        "numberRoommates",
        "basicRentOrRepayment",
        "heatingCosts",
        "additionalCosts",
        "costPaidByPetitioner",
        "hasPropertyLoans",
        "propertyLoans",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_PAYMENT_OBLIGATIONS]: {
      processCostSupport: ["hasOtherPaymentObligations", "paymentObligations"],
    },
    [applicationStatuses.APPLICATION_STATUS_SPECIAL_BURDENS]: {
      processCostSupport: ["hasSpecialFinancialBurdens", "specialFinancialBurdens"],
    },
    [applicationStatuses.APPLICATION_STATUS_RECEIPTS]: {
      processCostSupport: [],
    },
    [applicationStatuses.APPLICATION_STATUS_SUMMARY]: {},
    [applicationStatuses.APPLICATION_STATUS_CHECKED]: {},
  });

  floatFields = [
    "incomeEmploymentAmount",
    "costCoverageAmount",
    "incomeSelfEmploymentAmount",
    "incomeRentAmount",
    "incomeAssetsAmount",
    "incomeChildAllowanceAmount",
    "incomeLivingAmount",
    "incomeAlimonyAmount",
    "incomePensionAmount",
    "incomeUnemploymentAllowanceAmount",
    "incomeUnemploymentAllowance2Amount",
    "incomeSicknessAmount",
    "incomeParentalAllowanceAmount",
    "incomeOther1Amount",
    "incomeOther2Amount",
    "taxAmount",
    "socialSecurityAmount",
    "otherInsuranceAmount",
    "mobilityCostAmount",
    "otherExpensesAmount",
    "realEstate1Value",
    "cashAmount",
    "basicRentOrRepayment",
    "heatingCosts",
    "additionalCosts",
    "costPaidByPetitioner",
  ];

  dateFields = ["birthDate"];

  //fields that don't exist in the database but are needed to manage subentity array entries
  connectedCountFields = [
    {
      entityArray: "alimonyPersons",
      countField: "numberOfAlimonyPersons",
      entityFloatToStringFields: ["income", "alimony"],
    },
    {
      entityArray: "bankAccounts",
      countField: "numberOfBankAccounts",
      entityFloatToStringFields: ["amount"],
    },
    {
      entityArray: "vehicles",
      countField: "numberOfVehicles",
      entityFloatToStringFields: ["value"],
    },
    {
      entityArray: "valuables",
      countField: "numberofValuables",
      entityFloatToStringFields: ["value"],
    },
    {
      entityArray: "propertyLoans",
      countField: "numberOfPropertyLoans",
      entityFloatToStringFields: ["repayment", "outstandingDebt"],
    },
    {
      entityArray: "otherAssets",
      countField: "numberOfOtherAssets",
      entityFloatToStringFields: ["value"],
    },
    {
      entityArray: "paymentObligations",
      countField: "numberOfPaymentObligations",
      entityFloatToStringFields: ["repayment", "outstandingDebt", "petitionerShare"],
    },
    {
      entityArray: "pensionInsurances",
      countField: "numberofPensionInsurances",
      entityFloatToStringFields: ["value"],
    },
    {
      entityArray: "specialFinancialBurdens",
      countField: "numberOfSpecialFinancialBurdens",
      entityFloatToStringFields: ["petitionerShare"],
    },
  ];

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product: processCostSupport,
    customer,
    updateMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};

    if (_has(fields, "customer")) {
      let customerData = _pick(formData.customer, fields.customer);
      _forEach(customerData, (value, key) => {
        if (this.dateFields.includes(key)) {
          customerData[key] = toDate(value);
        }
      });
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "petitioner")) {
      let petitionerData = _pick(formData.petitioner, fields.petitioner);
      _forEach(petitionerData, (value, key) => {
        if (this.dateFields.includes(key)) {
          petitionerData[key] = toDate(value);
        }
      });
      await updateMutation.mutateAsync({
        uri: "/people",
        id: processCostSupport.petitioner.id,
        data: sanitizePersonData(petitionerData),
      });
    }

    if (_has(fields, "petitioner.addresses[0]")) {
      const addressData = _pick(formData.petitioner.addresses[0], fields["petitioner.addresses[0]"]);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: processCostSupport.petitioner.addresses[0].id,
        data: addressData,
      });
    }

    if (_has(fields, "legalRepresentative")) {
      const petitionerData = sanitizePersonData(_pick(formData.legalRepresentative, fields.legalRepresentative));
      await updateMutation.mutateAsync({
        uri: "/people",
        id: processCostSupport.legalRepresentative.id,
        data: petitionerData,
      });
    }

    if (_has(fields, "legalRepresentative.addresses[0]")) {
      const addressData = _pick(formData.legalRepresentative.addresses[0], fields["legalRepresentative.addresses[0]"]);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: processCostSupport.legalRepresentative.addresses[0].id,
        data: addressData,
      });
    }

    if (_has(fields, "alimonyObligatedPerson")) {
      const petitionerData = sanitizePersonData(_pick(formData.alimonyObligatedPerson, fields.alimonyObligatedPerson));
      await updateMutation.mutateAsync({
        uri: "/people",
        id: processCostSupport.alimonyObligatedPerson.id,
        data: petitionerData,
      });
    }

    let processCostSupportUpdate: Partial<ProcessCostSupport> = {};
    if (nextApplicationStatus) {
      processCostSupportUpdate.applicationStatus = nextApplicationStatus;
    }

    if (_has(fields, "processCostSupport")) {
      processCostSupportUpdate = _merge(processCostSupportUpdate, _pick(formData, fields.processCostSupport));
      processCostSupportUpdate = this.handleProcessCostSupportFields(processCostSupportUpdate);
    }

    await updateMutation.mutateAsync({
      uri: "/process_cost_supports",
      id: processCostSupport.id,
      data: processCostSupportUpdate,
    });
  };

  handleProcessCostSupportFields = (data: Partial<ProcessCostSupport>) => {
    let resultData = _merge({}, data);

    const numberFields = ["realEstate1Size", "realEstate1NumberApartments", "appartmentSize", "numberRooms", "numberRoommates"];

    _forEach(resultData, (value, key) => {
      if (value === "yes" || value === "no") {
        resultData[key] = value === "yes";
      } else if (this.floatFields.includes(key)) {
        resultData[key] = toFloat(value);
      } else if (numberFields.includes(key)) {
        resultData[key] = parseInt(value);
      }
    });

    this.connectedCountFields.forEach((field) => {
      if (!resultData.hasOwnProperty(field.entityArray)) {
        return;
      }
      resultData[field.entityArray].forEach((entity: Record<string, any>) => {
        field.entityFloatToStringFields.forEach((floatToStringField) => {
          entity[floatToStringField] = entity[floatToStringField].replace(".", ",");
        });
      });
    });

    return resultData;
  };
  prepareFormData = (processCostSupport: ProcessCostSupport, customer: Customer) => {
    let resultData = _merge({}, processCostSupport);
    resultData.customer = _merge({}, customer);
    resultData.petitioner.birthDate = fromDate(resultData.petitioner.birthDate);
    resultData.customer.person.birthDate = fromDate(resultData.customer.person.birthDate);

    _forEach(resultData, (value, key) => {
      if (_isBoolean(value)) {
        resultData[key] = value ? "yes" : "no";
      } else if (this.floatFields.includes(key)) {
        resultData[key] = fromFloat(value);
      } else {
        resultData[key] = value;
      }
    });

    _forEach(this.connectedCountFields, (connectedCountFieldPair) => {
      if (resultData[connectedCountFieldPair.entityArray]) {
        resultData[connectedCountFieldPair.countField] = fromNumber(resultData[connectedCountFieldPair.entityArray].length);
      }
    });

    return resultData;
  };

  getAvailableSteps = (formData: Record<string, any>) => {
    let availableSteps = [];
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INTRO]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_LEGAL_EXPENSES_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PERSONAL]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_JOB]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_REPRESENTATIVE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_MAINTENANCE_CLAIM]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_ALIMONY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SOCIAL_WELFARE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_EMPLOYMENT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_SELFEMPLOYMENT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_RENT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_ASSETS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_CHILD_ALLOWANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_LIVING]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_ALIMONY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_PENSION]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_UNEMPLOYED_ALLOWANCE_1]);
    if (formData.hasIncomeUnemploymentAllowance === "no") {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_UNEMPLOYED_ALLOWANCE_2]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_SICKNESS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_PARENTAL_ALLOWANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_OTHER]);
    if (this.hasNoIncome(formData)) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCOME_DESCRIPTIVE]);
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_BANK_ACCOUNT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_REALESTATE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_VEHICLES]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CASH]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PENSION_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_OTHER_ASSETS]);

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_TAX]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SOCIAL_SECURITY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INSURANCES]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_MOBILITY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_OTHER_EXPENSES]);

    if (formData.hasIncomeLiving === "no") {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_LIVING_TYPE]);
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PAYMENT_OBLIGATIONS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SPECIAL_BURDENS]);

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_RECEIPTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CHECKING]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CHECKED]);

    return availableSteps;
  };

  hasNoIncome = (formData: Record<string, any>) => {
    const incomeFields = [
      "hasIncomeEmployment",
      "hasIncomeSelfEmployment",
      "hasIncomeRent",
      "hasIncomeAssets",
      "hasIncomeChildAllowance",
      "hasIncomeLiving",
      "hasIncomeAlimony",
      "hasIncomePension",
      "hasIncomeUnemploymentAllowance",
      "hasIncomeUnemploymentAllowance2",
      "hasIncomeSickness",
      "hasIncomeParentalAllowance",
      "hasIncomeOther1",
      "hasIncomeOther2",
    ];

    return _reduce(formData, (result, value, key) => {
      if (!incomeFields.includes(key)) {
        return !!result;
      }
      return result && value !== "yes";
    });
  };
}

export default new FormService();
